<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="파트너 커뮤니티"
        subtitle="파트너들과 정보를 공유하세요"
        icon="community"
        iconColor="indigo"
      >
        <template #actions>
          <button
            @click="showWriteModal = true"
            class="px-4 py-2 bg-blue-600 text-white text-[14px] font-bold rounded-xl hover:bg-blue-700 transition-colors"
          >
            글쓰기
          </button>
        </template>
      </PartnersHeader>
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <!-- 카테고리 필터 -->
        <div class="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
          <button
            v-for="category in categories"
            :key="category.id"
            @click="selectedCategory = category.id"
            class="px-4 py-2 text-[13px] font-bold rounded-xl whitespace-nowrap transition-colors"
            :class="selectedCategory === category.id
              ? 'bg-blue-600 text-white'
              : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'"
          >
            {{ category.label }}
          </button>
        </div>

        <!-- 로딩 -->
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <!-- 게시글 목록 -->
        <div v-else-if="filteredPosts.length > 0" class="space-y-3">
          <div
            v-for="post in filteredPosts"
            :key="post.id"
            @click="viewPost(post)"
            class="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md transition-shadow cursor-pointer"
          >
            <div class="flex items-start gap-3">
              <!-- 작성자 아바타 -->
              <div class="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                <span class="text-white text-[14px] font-bold">{{ getInitial(post.authorName) }}</span>
              </div>

              <div class="flex-1 min-w-0">
                <!-- 카테고리 + 제목 -->
                <div class="flex items-center gap-2 mb-1">
                  <span class="px-2 py-0.5 text-[10px] font-bold rounded" :class="getCategoryClass(post.category)">
                    {{ getCategoryLabel(post.category) }}
                  </span>
                  <h3 class="text-[15px] font-bold text-slate-900 truncate">{{ post.title }}</h3>
                </div>

                <!-- 내용 미리보기 -->
                <p class="text-[13px] text-slate-500 line-clamp-2 mb-2">{{ post.content }}</p>

                <!-- 메타 정보 -->
                <div class="flex items-center gap-4 text-[12px] text-slate-400">
                  <span>{{ post.authorDisplayName || post.authorName }}</span>
                  <span>{{ formatDate(post.createdAt) }}</span>
                  <span class="flex items-center gap-1">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                    {{ post.views || 0 }}
                  </span>
                  <span class="flex items-center gap-1">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                    </svg>
                    {{ post.commentCount || 0 }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 게시글 없음 -->
        <div v-else class="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <div class="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"/>
            </svg>
          </div>
          <p class="text-[15px] font-medium text-slate-500 mb-4">아직 게시글이 없습니다</p>
          <button
            @click="showWriteModal = true"
            class="px-5 py-2.5 bg-blue-600 text-white text-[14px] font-bold rounded-xl hover:bg-blue-700 transition-colors"
          >
            첫 글 작성하기
          </button>
        </div>
      </div>
    </div>

    <!-- 글쓰기 모달 -->
    <div v-if="showWriteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div class="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 class="text-[16px] font-bold text-slate-900">글쓰기</h3>
          <button @click="showWriteModal = false" class="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <form @submit.prevent="createPost" class="p-6 space-y-4">
          <div>
            <label class="block text-[13px] font-bold text-slate-700 mb-2">카테고리</label>
            <select
              v-model="newPost.category"
              class="w-full px-4 py-3 rounded-xl border border-slate-200 text-[14px] font-medium text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
              required
            >
              <option value="">카테고리 선택</option>
              <option v-for="cat in categories.slice(1)" :key="cat.id" :value="cat.id">{{ cat.label }}</option>
            </select>
          </div>

          <div>
            <label class="block text-[13px] font-bold text-slate-700 mb-2">제목</label>
            <input
              v-model="newPost.title"
              type="text"
              placeholder="제목을 입력하세요"
              class="w-full px-4 py-3 rounded-xl border border-slate-200 text-[14px] font-medium text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
              required
            />
          </div>

          <div>
            <label class="block text-[13px] font-bold text-slate-700 mb-2">내용</label>
            <textarea
              v-model="newPost.content"
              placeholder="내용을 입력하세요"
              rows="8"
              class="w-full px-4 py-3 rounded-xl border border-slate-200 text-[14px] font-medium text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition resize-none"
              required
            ></textarea>
          </div>

          <div class="flex gap-3 justify-end pt-2">
            <button
              type="button"
              @click="showWriteModal = false"
              class="px-4 py-2.5 bg-slate-100 text-slate-700 text-[14px] font-medium rounded-xl hover:bg-slate-200 transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              :disabled="creating"
              class="px-4 py-2.5 bg-blue-600 text-white text-[14px] font-bold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {{ creating ? '등록 중...' : '등록' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 게시글 상세 모달 -->
    <div v-if="selectedPost" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <!-- 헤더 -->
        <div class="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <div class="flex items-center gap-2">
            <span class="px-2 py-0.5 text-[10px] font-bold rounded" :class="getCategoryClass(selectedPost.category)">
              {{ getCategoryLabel(selectedPost.category) }}
            </span>
          </div>
          <button @click="selectedPost = null" class="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- 본문 -->
        <div class="p-6">
          <h2 class="text-[20px] font-bold text-slate-900 mb-3">{{ selectedPost.title }}</h2>

          <div class="flex items-center gap-3 mb-6">
            <div class="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
              <span class="text-white text-[14px] font-bold">{{ getInitial(selectedPost.authorName) }}</span>
            </div>
            <div>
              <p class="text-[14px] font-bold text-slate-900">{{ selectedPost.authorDisplayName || selectedPost.authorName }}</p>
              <p class="text-[12px] text-slate-400">{{ formatDate(selectedPost.createdAt) }} · 조회 {{ selectedPost.views || 0 }}</p>
            </div>
          </div>

          <div class="text-[15px] text-slate-700 leading-relaxed whitespace-pre-wrap mb-8">
            {{ selectedPost.content }}
          </div>

          <!-- 댓글 섹션 -->
          <div class="border-t border-slate-100 pt-6">
            <h4 class="text-[15px] font-bold text-slate-900 mb-4">댓글 {{ comments.length }}개</h4>

            <!-- 댓글 입력 -->
            <form @submit.prevent="createComment" class="mb-6">
              <div class="flex gap-2">
                <input
                  v-model="newComment"
                  type="text"
                  placeholder="댓글을 입력하세요"
                  class="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-[14px] font-medium text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                />
                <button
                  type="submit"
                  :disabled="!newComment.trim() || submittingComment"
                  class="px-4 py-2.5 bg-blue-600 text-white text-[14px] font-bold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  등록
                </button>
              </div>
            </form>

            <!-- 댓글 목록 -->
            <div v-if="comments.length > 0" class="space-y-4">
              <div v-for="comment in comments" :key="comment.id" class="flex gap-3">
                <div class="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                  <span class="text-slate-600 text-[12px] font-bold">{{ getInitial(comment.authorName) }}</span>
                </div>
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-1">
                    <span class="text-[13px] font-bold text-slate-900">{{ comment.authorDisplayName || comment.authorName }}</span>
                    <span class="text-[11px] text-slate-400">{{ formatDate(comment.createdAt) }}</span>
                  </div>
                  <p class="text-[14px] text-slate-700">{{ comment.content }}</p>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-6">
              <p class="text-[13px] text-slate-400">아직 댓글이 없습니다</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, query, orderBy, getDocs, addDoc, updateDoc, doc, increment } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'

export default {
  name: 'PartnerCommunityView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const { user, userProfile } = useAuth()

    const loading = ref(true)
    const creating = ref(false)
    const submittingComment = ref(false)
    const posts = ref([])
    const comments = ref([])
    const selectedCategory = ref('all')
    const showWriteModal = ref(false)
    const selectedPost = ref(null)
    const newComment = ref('')

    const newPost = ref({
      category: '',
      title: '',
      content: ''
    })

    const categories = [
      { id: 'all', label: '전체' },
      { id: 'free', label: '자유게시판' },
      { id: 'info', label: '정보공유' },
      { id: 'qna', label: '질문/답변' },
      { id: 'deal', label: '거래/홍보' }
    ]

    const filteredPosts = computed(() => {
      if (selectedCategory.value === 'all') {
        return posts.value
      }
      return posts.value.filter(p => p.category === selectedCategory.value)
    })

    const fetchPosts = async () => {
      loading.value = true
      try {
        const q = query(
          collection(db, 'partnerCommunity'),
          orderBy('createdAt', 'desc')
        )
        const snapshot = await getDocs(q)
        posts.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (error) {
        console.error('게시글 조회 실패:', error)
      } finally {
        loading.value = false
      }
    }

    const fetchComments = async (postId) => {
      try {
        const q = query(
          collection(db, 'partnerCommunity', postId, 'comments'),
          orderBy('createdAt', 'asc')
        )
        const snapshot = await getDocs(q)
        comments.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (error) {
        console.error('댓글 조회 실패:', error)
      }
    }

    const viewPost = async (post) => {
      selectedPost.value = post

      // 조회수 증가
      try {
        await updateDoc(doc(db, 'partnerCommunity', post.id), {
          views: increment(1)
        })
        post.views = (post.views || 0) + 1
      } catch (error) {
        console.error('조회수 업데이트 실패:', error)
      }

      // 댓글 로드
      await fetchComments(post.id)
    }

    const createPost = async () => {
      if (!newPost.value.category || !newPost.value.title || !newPost.value.content) return
      if (!user.value) return

      creating.value = true
      try {
        await addDoc(collection(db, 'partnerCommunity'), {
          category: newPost.value.category,
          title: newPost.value.title,
          content: newPost.value.content,
          authorId: user.value.uid,
          authorName: userProfile.value?.name || user.value.email,
          authorDisplayName: userProfile.value?.displayName || userProfile.value?.name,
          views: 0,
          commentCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })

        newPost.value = { category: '', title: '', content: '' }
        showWriteModal.value = false
        await fetchPosts()
      } catch (error) {
        console.error('게시글 등록 실패:', error)
        alert('게시글 등록에 실패했습니다.')
      } finally {
        creating.value = false
      }
    }

    const createComment = async () => {
      if (!newComment.value.trim() || !selectedPost.value || !user.value) return

      submittingComment.value = true
      try {
        await addDoc(collection(db, 'partnerCommunity', selectedPost.value.id, 'comments'), {
          content: newComment.value.trim(),
          authorId: user.value.uid,
          authorName: userProfile.value?.name || user.value.email,
          authorDisplayName: userProfile.value?.displayName || userProfile.value?.name,
          createdAt: new Date().toISOString()
        })

        // 댓글 수 증가
        await updateDoc(doc(db, 'partnerCommunity', selectedPost.value.id), {
          commentCount: increment(1)
        })
        selectedPost.value.commentCount = (selectedPost.value.commentCount || 0) + 1

        newComment.value = ''
        await fetchComments(selectedPost.value.id)
      } catch (error) {
        console.error('댓글 등록 실패:', error)
        alert('댓글 등록에 실패했습니다.')
      } finally {
        submittingComment.value = false
      }
    }

    const getInitial = (name) => {
      if (!name) return '?'
      return name.charAt(0).toUpperCase()
    }

    const getCategoryLabel = (categoryId) => {
      const cat = categories.find(c => c.id === categoryId)
      return cat ? cat.label : categoryId
    }

    const getCategoryClass = (categoryId) => {
      const classes = {
        free: 'bg-slate-100 text-slate-700',
        info: 'bg-blue-100 text-blue-700',
        qna: 'bg-amber-100 text-amber-700',
        deal: 'bg-emerald-100 text-emerald-700'
      }
      return classes[categoryId] || 'bg-slate-100 text-slate-700'
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date

      if (diff < 60 * 60 * 1000) return `${Math.floor(diff / (60 * 1000))}분 전`
      if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / (60 * 60 * 1000))}시간 전`
      if (diff < 7 * 24 * 60 * 60 * 1000) return `${Math.floor(diff / (24 * 60 * 60 * 1000))}일 전`
      return `${date.getMonth() + 1}월 ${date.getDate()}일`
    }

    onMounted(() => {
      fetchPosts()
    })

    return {
      loading,
      creating,
      submittingComment,
      posts,
      comments,
      selectedCategory,
      showWriteModal,
      selectedPost,
      newPost,
      newComment,
      categories,
      filteredPosts,
      viewPost,
      createPost,
      createComment,
      getInitial,
      getCategoryLabel,
      getCategoryClass,
      formatDate
    }
  }
}
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
